import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import DjangoMessages from 'components/DjangoMessages';
import FormActions from 'components/Form/FormActions';
import SubmitButton from 'components/Form/SubmitButton';
import Information from 'components/Information';
import SectionHeader from 'components/SectionHeader';
import {useBackend} from 'state/backend';
import {setUrl} from 'utils/request';
import {onApiErrorAxios, onApiSuccess} from 'utils/request-axios/utils';
import useRevokeEmployee from './services';
import {MemberRevokeBackendData} from './types';

const RevokeApiEmployee = () => {
    const {t} = useTranslation('Team.Member.Revoke', {keyPrefix: 'api'});
    const {
        data: {apiName, isApi, partnerName},
    } = useBackend<MemberRevokeBackendData>();

    if (!isApi) return null;

    return (
        <>
            <SectionHeader level={1} title={t('title', {apiName})} />
            <p>
                <Trans
                    components={[<b key={0} />, <b key={0} />]}
                    i18nKey="aboutTo"
                    t={t}
                    values={{apiName, partnerName}}
                />
            </p>
            <FormActions className="mt-4">
                <SubmitButton kind="destructive">{t('submit')}</SubmitButton>
            </FormActions>
        </>
    );
};

const RevokeEmployee = () => {
    const {t} = useTranslation('Team.Member.Revoke', {keyPrefix: 'employee'});
    const {
        data: {
            accountName,
            employeeName,
            isApi,
            partnerName,
            scheduledSendingOrderCount,
        },
    } = useBackend<MemberRevokeBackendData>();

    if (isApi) return null;

    return (
        <>
            <SectionHeader level={1} title={t('title')} />
            <div className="mt-4">{t('details')}</div>
            <ul>
                <li>
                    <Trans
                        components={[<b key={0} />]}
                        i18nKey="detailsName"
                        t={t}
                        values={{employeeName}}
                    />
                </li>
                {partnerName && accountName ? (
                    <li>
                        <Trans
                            components={[<b key={0} />, <b key={1} />]}
                            i18nKey="detailsCompanyPartnerAndAccount"
                            t={t}
                            values={{accountName, partnerName}}
                        />
                    </li>
                ) : partnerName ? (
                    <li>
                        <Trans
                            components={[<b key={0} />]}
                            i18nKey="detailsCompanyPartner"
                            t={t}
                            values={{partnerName}}
                        />
                    </li>
                ) : null}
            </ul>
            <p>
                <Trans
                    components={[<b key={0} />]}
                    i18nKey="afterRevoking"
                    t={t}
                    values={{employeeName}}
                />
            </p>
            <ul>
                <li>{t('afterRevokingNoMoreAccess')}</li>
                <li>{t('afterRevokingDocumentsAndHistory')}</li>
            </ul>
            {scheduledSendingOrderCount && scheduledSendingOrderCount > 0 ? (
                <Information className="mt-4" type="alert">
                    <Trans
                        components={[<b key={0} />, <b key={1} />]}
                        i18nKey="revokingAutomaticallyDiscards"
                        t={t}
                        values={{employeeName, scheduledSendingOrderCount}}
                    />
                </Information>
            ) : null}
            <FormActions className="mt-4">
                <SubmitButton kind="destructive">{t('revoke')}</SubmitButton>
            </FormActions>
        </>
    );
};

const MemberRevoke = () => {
    const methods = useForm();
    const {
        data: {mid},
    } = useBackend<MemberRevokeBackendData>();

    const {mutateAsync} = useRevokeEmployee();

    const handleSubmit = () => {
        mutateAsync({mid})
            .then(
                onApiSuccess({
                    onSuccess: () => setUrl(window.Urls.employee_list_view()),
                }),
            )
            .catch(onApiErrorAxios(methods));
    };

    return (
        <div>
            <DjangoMessages />
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <RevokeApiEmployee />
                    <RevokeEmployee />
                </form>
            </FormProvider>
        </div>
    );
};

export default MemberRevoke;
